import { Button, Grid, Tooltip } from '@material-ui/core';
import Spin from 'components/Spin';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import SelectCreditCard from 'v2/components/Shared/SelectCreditCard';
import { useSubscription } from 'v2/hooks/useSubscription';
import { EPaymentStatus } from 'v2/interfaces/shopping';
import { customerFactory } from 'v2/services/customer';
import { dateService } from 'v2/services/date';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import shoppingService from 'v2/services/shopping/subscription';

import type { ICard } from 'v2/components/Shared/SelectCreditCard';

import JustifyModal, { JustifyType } from './justifyModal';

type SubscriptionButtonsProps = {
  id: number;
};

const SubscriptionButtons = ({ id }: SubscriptionButtonsProps) => {
  const [justifyModal, setJustifyModal] = useState(false);
  const [justifyType, setJustifyType] = useState<JustifyType>(JustifyType.SUSPEND);
  const [hideCb, shouldHideActions] = useCallbackObservable((id) => {
    if (!id) return of(null);
    return shoppingService.canCancel(id);
  }, []);

  useEffect(() => {
    hideCb(id);
  }, [id, hideCb]);

  const onCloseJustifyModal = () => {
    setJustifyModal(false);
  };

  const onJustify = (type: JustifyType) => {
    setJustifyType(type);
    setJustifyModal(true);
  };

  if (shouldHideActions === null) return <Spin />;

  if (!id || !shouldHideActions.data) return null;

  return (
    <>
      <ChangePlan />
      <Suspend>
        <Grid item xs={12} md={6}>
          <Button onClick={() => onJustify(JustifyType.SUSPEND)} color='primary' style={{ background: '#dc3f53', color: '#ffff' }} fullWidth>
            Suspender
          </Button>
        </Grid>
      </Suspend>
      <Cancel>
        <Grid item xs={12} md={6}>
          <Button onClick={() => onJustify(JustifyType.CANCEL)} color='primary' style={{ background: '#dc3f53', color: '#ffff' }} fullWidth>
            Cancelar
          </Button>
        </Grid>
      </Cancel>
      <Reactivate>
        <Grid item xs={12} md={6}>
          <Button onClick={() => onJustify(JustifyType.REACTIVE)} variant='outlined' color='primary' fullWidth>
            Reativar
          </Button>
        </Grid>
      </Reactivate>
      <JustifyModal open={justifyModal} type={justifyType} onClose={onCloseJustifyModal} />
    </>
  );
};

const SubscriptionActions = ({ onClose }: { onClose: () => void }) => {
  const [open, setOpen] = useState<boolean>(false);

  useObservable(() => customerFactory.loadCardsListSubscriptions(), []);
  const [shopping] = useObservable(() => shoppingService.getCurrent(), []);
  const [cardsList] = useObservable(() => customerFactory.getCardsListSubscriptions(), []);
  const [isLoadingCards] = useObservable(() => customerFactory.loadingCardsSubscriptions(), []);
  const openPaymentMethod = useCallback(() => {
    setOpen(true);
  }, []);

  const closePaymentMethod = useCallback(() => {
    setOpen(false);
  }, []);

  const onSelectCard = useCallback(
    (card: ICard) => {
      setOpen(false);
      shoppingService
        .changeSubscriptionPayment(shopping.id, card)
        .pipe(tap(() => shoppingService.setPagination({ per_page: 50, current_page: 1 })))
        .subscribe(() => {
          toast.success('Forma de pagamento alterada com sucesso!');
          onClose();
        }, toast.error);
    },
    [onClose, shopping]
  );

  return (
    <>
      <Grid spacing={2} container justify='space-between'>
        <ChangePaymentMethod>
          <Grid item xs={12}>
            <Button color='primary' variant='outlined' onClick={openPaymentMethod} fullWidth>
              Trocar forma de pagamento
            </Button>
          </Grid>
        </ChangePaymentMethod>
        <SubscriptionButtons id={shopping?.id} />
      </Grid>
      <SelectCreditCard isOpen={open} onClose={closePaymentMethod} onFinish={onSelectCard} cardsList={cardsList} isLoadingCards={isLoadingCards} />
    </>
  );
};

const ChangePaymentMethod = ({ children }: { children: React.ReactNode }) => {
  const [shopping] = useObservable(() => shoppingService.getCurrent(), []);

  if (!!shopping?.product.allows_card && shopping?.payment_status.id !== EPaymentStatus.CANCELADO) {
    return <>{children}</>;
  }

  return null;
};

const Cancel = ({ children }: { children: React.ReactNode }) => {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [shopping] = useObservable(() => shoppingService.getCurrent(), []);

  if (shopping?.payment_status.id === EPaymentStatus.CANCELADO) {
    return null;
  }

  if (shopping?.payment_status.id === EPaymentStatus.FINALIZADO) {
    return null;
  }

  if (user?.id !== shopping?.producer.id && user?.id !== shopping?.customer.id) {
    return null;
  }

  return <>{children}</>;
};

const Suspend = ({ children }: { children: React.ReactNode }) => {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [shopping] = useObservable(() => shoppingService.getCurrent(), []);

  if (shopping?.payment_status.id === EPaymentStatus.CANCELADO) {
    return null;
  }

  if (shopping?.payment_status.id === EPaymentStatus.SUSPENSO) {
    return null;
  }

  if (shopping?.payment_status.id === EPaymentStatus.FINALIZADO) {
    return null;
  }

  if (user?.id !== shopping?.producer.id && user?.id !== shopping?.customer.id) {
    return null;
  }

  return <>{children}</>;
};

const Reactivate = ({ children }: { children: React.ReactNode }) => {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [shopping] = useObservable(() => shoppingService.getCurrent(), []);

  if (shopping?.payment_status.id !== EPaymentStatus.SUSPENSO) {
    return null;
  }

  if (user?.id !== shopping?.producer.id && user?.id !== shopping?.customer.id) {
    return null;
  }

  return <>{children}</>;
};

const ChangePlan = () => {
  const history = useHistory();
  const [disabledChangePlan, setDisabledChangePlan] = useState(false);
  const [shopping, isLoadingShopping] = useObservable(() => shoppingService.getCurrent(), []);

  const subscriptionQuery = useSubscription(Number(shopping?.id), { enabled: !!shopping?.id && !isLoadingShopping });
  const subscription = subscriptionQuery.data;
  const dateUnlockChangePlan = subscription?.changed_plan_at && dateService.addMonths(dateService.stringToDate(subscription.changed_plan_at), 3);
  useEffect(() => {
    if (dateUnlockChangePlan && dateService.isFutureDate(dateUnlockChangePlan)) {
      setDisabledChangePlan(true);
    }
  }, [dateUnlockChangePlan]);

  if (!shopping) return null;

  const inactiveStatus = [EPaymentStatus.SUSPENSO, EPaymentStatus.FINALIZADO, EPaymentStatus.CANCELADO];

  if (!shopping?.product.allows_card || inactiveStatus.includes(shopping.payment_status.id)) return null;
  if (subscriptionQuery.isLoading) {
    return (
      <div style={{ width: '100%', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!subscription || !subscription.allows_change_plan) return null;

  return (
    <Grid item xs={12}>
      <Tooltip
        title={disabledChangePlan ? `Será permitido trocar de plano em ${dateService.dateToString(dateUnlockChangePlan, 'dd/MM/yyyy')}` : ''}
        placement='top'
      >
        <span>
          <Button
            disabled={disabledChangePlan}
            onClick={() => history.push(`/producer/subscriptions/${shopping?.id}/change-plan`)}
            color='primary'
            fullWidth
            id='change-plan-action-button'
          >
            Trocar plano
          </Button>
        </span>
      </Tooltip>
    </Grid>
  );
};

export default SubscriptionActions;
